import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEsMovil } from '../../indicadores/hooks/useEsMovil';
import { MetasApiError, MetasColaboradorService } from '../services/metasColaborador.service';
import type {
  AlcanceDeTienda,
  AlcancesSemana,
  BandaAlcance,
  ColaboradorConAlcance,
  MiAlcance,
  TiendaConAlcance,
  TiendaRef,
  TramoAlcance,
} from '../types/metas.types';
import {
  anchoBarra,
  ariaTramos,
  cadenasDe,
  CLASE_FONDO_BANDA,
  corteEnMeta,
  etiquetaAlcanceGlobal,
  etiquetaCortaTramo,
  motivoCorto,
  motivoTiendaDetalle,
  motivoTiendaCorto,
  motivoTiendaTexto,
  ORDEN_BANDAS,
  pctAlcance,
  POSICION_MARCA_100,
  TIENDAS_A_LA_VISTA,
  ventaAlTexto,
} from '../utils/alcances';
import { eligeSupervisor, nombreTienda, regionalesDe, supervisorDe, supervisoresDe, textoTiendas } from '../utils/autorizacion';
import { guardarArchivo } from '../utils/descarga';
import { veTodasLasTiendas } from '../utils/direccion';
import { ETIQUETA_ESTADO } from '../utils/estado';
import { nombrePropio, pesosEnteros } from '../utils/formato';
import { CLASE_BOTON, CLASE_BOTON_TEXTO, CLASE_PANEL, CLASES_CAMPO, cx } from './ui/clases';
import { Field } from './ui/Field';
import { KpiCard } from './ui/KpiCard';
import { Pill } from './ui/Pill';

type AvisoToast = (severidad: 'success' | 'warn' | 'error', resumen: string, detalle?: string) => void;

interface Props {
  alcance: MiAlcance;
  /** Lunes de la semana CERRADA que se mide (la pantalla sólo ofrece semanas cerradas). */
  lunes: string;
  onAviso: AvisoToast;
  /** La pestaña está a la vista: hasta que se abre la primera vez no se pide nada. */
  activa?: boolean;
  /** La tienda del enlace (`?tienda=`), si el enlace era de esta pestaña. */
  tiendaInicial?: TiendaRef | null;
  /** La tienda abierta en el detalle, para escribirla en el enlace. */
  onTienda?: (t: TiendaRef | null) => void;
}

export const TITULO_TIENDAS = 'Alcance por tienda';
export const SUBTITULO_TIENDAS = 'Venta real sin IVA vs. meta de tienda';
export const NOTA_ALCANCE =
  'Alcance = venta real sin IVA ÷ meta autorizada. Solo las metas autorizadas entran al cálculo de comisiones.';

const mismaTienda = (a: TiendaRef | null | undefined, b: TiendaRef | null | undefined): boolean =>
  !!a && !!b && a.chain === b.chain && a.num === b.num;

const IconoCandado = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const IconoAviso = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0">
    <path d="M12 3l10 18H2z" />
    <path d="M12 10v5M12 18h.01" />
  </svg>
);

const IconoExcel = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-metas-exito">
    <path d="M14 3H6v18h12V7z" />
    <path d="M14 3v4h4M9.5 12l5 5M14.5 12l-5 5" />
  </svg>
);

/**
 * La barra de alcance de una tienda (`BarraAlcanceTienda`): escala hasta 130 %
 * con una marca en 100 %. Es decorativa: el % va escrito al lado.
 */
export const BarraAlcance: React.FC<{ pct: number | null; banda: BandaAlcance | null }> = ({ pct, banda }) => (
  <div className="relative h-2.5 w-full rounded-[5px] bg-metas-neutro-fondo" aria-hidden="true" data-testid="barra-alcance">
    {banda && pct !== null && (
      <div
        className={cx('absolute inset-y-0 left-0 rounded-[3px]', CLASE_FONDO_BANDA[banda])}
        style={{ width: `${anchoBarra(pct).toFixed(1)}%` }}
        data-banda={banda}
      />
    )}
    <span
      className="absolute -top-1 h-[18px] w-0.5 rounded-sm bg-metas-texto"
      style={{ left: `${POSICION_MARCA_100.toFixed(1)}%` }}
      data-testid="marca-100"
    />
  </div>
);

/** Punto de color + % (el color nunca va solo: el % y, para el lector, la banda). */
const PctConBanda: React.FC<{ pct: number; banda: BandaAlcance | null; tramos: TramoAlcance[] }> = ({ pct, banda, tramos }) => {
  const etiqueta = tramos.find((t) => t.banda === banda)?.etiqueta;
  return (
    <span className="inline-flex items-center gap-2">
      {banda && <span aria-hidden="true" className={cx('h-2 w-2 shrink-0 rounded-full', CLASE_FONDO_BANDA[banda])} data-banda={banda} />}
      <span className="num font-semibold">{pctAlcance(pct)}</span>
      {etiqueta && <span className="sr-only">, tramo {etiqueta}</span>}
    </span>
  );
};

/**
 * La pastilla gris de quien no tiene %: corta, con el motivo completo en el
 * `title` y para el lector. En escritorio va en UNA línea, como el diseño
 * (`.pill{height:24px;white-space:nowrap}`): si no cabe, se recorta con «…» y
 * el `title` la completa (F5-V5). Sólo en el teléfono, donde la columna es
 * angosta y la tarjeta crece sin romper nada, parte el texto.
 */
const PillMotivo: React.FC<{ corto: string; completo?: string | null; envolver?: boolean }> = ({ corto, completo, envolver }) => {
  const extra = completo && completo.replace(/\.$/, '') !== corto ? completo : null;
  return (
    <Pill
      tono="neutro"
      title={extra ?? corto}
      className={cx('max-w-full min-w-0', envolver && '!whitespace-normal py-0.5 text-left !h-auto min-h-6 leading-tight')}
    >
      <span className={envolver ? undefined : 'min-w-0 truncate'} data-pill-motivo="">
        {corto}
      </span>
      {extra && <span className="sr-only">: {extra}</span>}
    </Pill>
  );
};

/**
 * «Alcances» (fase 5, artboard `Alcances`): venta real SIN IVA contra meta, en
 * semanas CERRADAS.
 *
 * - La tienda se mide contra su meta de tienda (SISCOM) y la persona contra su
 *   meta AUTORIZADA; quien no la tiene muestra el motivo, no un %.
 * - Todo lo calcula la API (`GET …/alcances`): aquí no se divide nada. Si la
 *   semana no cerró o al snapshot le falta un día, la API manda la venta en
 *   `null` y el motivo en `aviso`, y la pantalla lo dice en vez de inventar.
 * - Cadena, regional y supervisor sólo ACOTAN lo que la cuenta ve. El regional
 *   (filtro) lo eligen el administrador y dirección (21-oct-2026), que ven
 *   todas las tiendas; el regional ve el suyo. El supervisor (21-oct-2026,
 *   pedido para el visor del regional) lo eligen el regional, el administrador
 *   y dirección, entre los de la cadena y el regional elegidos. Va a la API
 *   (`?supervisor=`), no se filtra aquí: así los indicadores de arriba y el
 *   Excel «para comisiones» se rehacen con SUS tiendas.
 * - Clic (o Enter) en una tienda carga sus colaboradores a la derecha. En el
 *   teléfono todo va en tarjetas apiladas, sin scroll de lado.
 */
export const AlcancesTab: React.FC<Props> = ({ alcance, lunes, onAviso, activa = true, tiendaInicial = null, onTienda }) => {
  // Tarjetas hasta 1023 px, como Captura y Autorización: a 768 las columnas fijas
  // (108, 108 y 120 px) cortaban el nombre de la tienda (auditoría del 22-oct-2026).
  const esMovil = useEsMovil(1023);
  const [abierta, setAbierta] = useState(!!activa);
  useEffect(() => {
    if (activa) setAbierta(true);
  }, [activa]);

  const [cadena, setCadena] = useState('');
  const [regional, setRegional] = useState('');
  const [supervisor, setSupervisor] = useState('');
  const [verTodas, setVerTodas] = useState(false);
  const [datos, setDatos] = useState<AlcancesSemana | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);

  const [elegida, setElegida] = useState<TiendaRef | null>(tiendaInicial);
  const [detalle, setDetalle] = useState<AlcanceDeTienda | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState<string | null>(null);
  const refDetalle = useRef<HTMLElement>(null);

  // ------------------------------------------------------------ lista
  const pedido = useRef(0);
  const cargar = useCallback(async () => {
    const n = ++pedido.current;
    setCargando(true);
    setError(null);
    try {
      const d = await MetasColaboradorService.alcancesSemana(lunes, { cadena, regional, supervisor });
      if (n !== pedido.current) return;
      setDatos(d);
      setCargando(false);
    } catch (e) {
      if (n !== pedido.current) return;
      setDatos(null);
      setError((e as Error).message);
      setCargando(false);
    }
  }, [lunes, cadena, regional, supervisor]);

  useEffect(() => {
    if (abierta) void cargar();
  }, [abierta, cargar]);

  const tiendas = useMemo(() => datos?.tiendas ?? [], [datos]);
  const medible = !!datos && datos.cerrada && datos.completo;
  /** «Que venza los martes» (30-sep): en plazo, el Excel para comisiones no se baja. */
  const bloqueoComisiones = datos?.comisiones?.bloqueo ?? null;

  // La tienda abierta tiene que estar en la lista. Si no (otro filtro, otra
  // semana, un enlace con una tienda ajena), en escritorio se abre la primera
  // —el panel derecho no se queda vacío—; en el teléfono, ninguna.
  useEffect(() => {
    if (!datos) return;
    if (elegida && tiendas.some((t) => mismaTienda(t, elegida))) return;
    const primera = !esMovil && tiendas[0] ? { chain: tiendas[0].chain, num: tiendas[0].num } : null;
    setElegida(primera);
  }, [datos, tiendas, elegida, esMovil]);

  // Una tienda abierta más allá de las 8 primeras (desde un enlace) se ve.
  useEffect(() => {
    if (!elegida) return;
    const i = tiendas.findIndex((t) => mismaTienda(t, elegida));
    if (i >= TIENDAS_A_LA_VISTA) setVerTodas(true);
  }, [elegida, tiendas]);

  const onTiendaRef = useRef(onTienda);
  onTiendaRef.current = onTienda;
  useEffect(() => {
    if (!datos) return;
    onTiendaRef.current?.(elegida);
  }, [elegida, datos]);

  // ------------------------------------------------------------ detalle
  const pedidoDetalle = useRef(0);
  const cargarDetalle = useCallback(async () => {
    const n = ++pedidoDetalle.current;
    setDetalle(null);
    setErrorDetalle(null);
    // Con la semana abierta la API responde 409: no se pide, se dice.
    if (!elegida || !datos?.cerrada) {
      setCargandoDetalle(false);
      return;
    }
    setCargandoDetalle(true);
    try {
      const d = await MetasColaboradorService.alcanceDeTienda(lunes, elegida.chain, elegida.num);
      if (n !== pedidoDetalle.current) return;
      // En el mismo paso que el dato: un `finally` aparte pintaba una vuelta más.
      setDetalle(d);
      setCargandoDetalle(false);
    } catch (e) {
      if (n !== pedidoDetalle.current) return;
      setErrorDetalle((e as Error).message);
      setCargandoDetalle(false);
    }
  }, [elegida, datos?.cerrada, lunes]);

  useEffect(() => {
    void cargarDetalle();
  }, [cargarDetalle]);

  // En el teléfono el detalle va DEBAJO de la lista: se lleva a la vista, pero
  // cuando YA llegó (F5-V8). Antes se desplazaba con el panel en «Cargando…»,
  // el final del documento topaba el desplazamiento y, al crecer el panel, el
  // detalle quedaba a media pantalla.
  const pendienteScroll = useRef(false);
  const elegir = (t: TiendaConAlcance) => {
    setElegida({ chain: t.chain, num: t.num });
    if (esMovil) pendienteScroll.current = true;
  };
  useEffect(() => {
    if (cargandoDetalle || !pendienteScroll.current) return;
    // Con la semana abierta no se pide el detalle: el aviso ya es lo que se lleva a la vista.
    if (!detalle && !errorDetalle && datos?.cerrada !== false) return;
    pendienteScroll.current = false;
    const el = refDetalle.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [detalle, errorDetalle, cargandoDetalle, datos?.cerrada, elegida]);

  // ------------------------------------------------------------ exportar
  const exportar = async () => {
    if (descargando) return;
    setDescargando(true);
    try {
      // Con los MISMOS filtros que la pantalla, supervisor incluido (21-oct-2026).
      guardarArchivo(await MetasColaboradorService.alcancesExcel(lunes, { cadena, regional, supervisor }));
    } catch (e) {
      const conflicto = e instanceof MetasApiError && e.status === 409;
      onAviso(conflicto ? 'warn' : 'error', 'No se pudo exportar', (e as Error).message);
    } finally {
      setDescargando(false);
    }
  };

  // ------------------------------------------------------------ filtros
  const cadenas = useMemo(() => cadenasDe(alcance.tiendas), [alcance.tiendas]);
  const tiendasDeCadena = useMemo(
    () => alcance.tiendas.filter((t) => !cadena || t.chain === cadena),
    [alcance.tiendas, cadena],
  );
  // El filtro de regional va por NOMBRE a la API; una tienda sin regional no se puede pedir así.
  const regionales = useMemo(
    () => regionalesDe(tiendasDeCadena.filter((t) => (t.regional ?? '').trim())),
    [tiendasDeCadena],
  );
  const misRegionales = alcance.alcances.filter((a) => a.tipo === 'regional');
  // Supervisor (21-oct-2026): los de la cadena y el regional elegidos. Como el
  // regional, va a la API por NOMBRE: una tienda sin supervisor no se puede pedir así.
  const filtraSupervisor = eligeSupervisor(alcance);
  const tiendasDelRegional = useMemo(
    () => tiendasDeCadena.filter((t) => !regional || t.regional === regional),
    [tiendasDeCadena, regional],
  );
  const supervisores = useMemo(
    () => supervisoresDe(tiendasDelRegional.filter((t) => (t.supervisor ?? '').trim())),
    [tiendasDelRegional],
  );
  /** ¿Sigue habiendo tiendas del supervisor elegido con esta cadena y este regional? */
  const supervisorEn = (c: string, r: string) =>
    !supervisor || alcance.tiendas.some((x) => (!c || x.chain === c) && (!r || x.regional === r) && supervisorDe(x) === supervisor);

  const cambiarFiltro = (f: () => void) => {
    f();
    setVerTodas(false);
  };

  // `select` nativo dentro de la caja del diseño (`.field select`), como el área
  // de Pendientes: 40 px (44 en el teléfono) y el teclado y el lector del
  // sistema. El Dropdown de PrimeReact medía 50 y no alineaba con el regional fijo.
  const claseCaja = cx(
    CLASES_CAMPO.normal,
    'relative max-md:h-metas-tactil [@media(pointer:coarse)]:h-metas-tactil focus-within:border-metas-primario focus-within:shadow-metas-anillo-sel',
  );
  const claseSelect =
    'h-full w-full min-w-0 cursor-pointer appearance-none border-0 bg-transparent p-0 pr-6 text-sm text-metas-texto outline-none max-md:text-base';
  const flecha = <i className="pi pi-chevron-down pointer-events-none absolute right-3 text-xs" aria-hidden="true" />;

  const campoCadena = (
    <Field etiqueta="Cadena" htmlFor="metas-alcances-cadena" className={esMovil ? 'w-full' : 'w-[200px]'}>
      <div className={claseCaja}>
        <select
          id="metas-alcances-cadena"
          value={cadena}
          onChange={(e) =>
            cambiarFiltro(() => {
              const c = e.target.value;
              setCadena(c);
              // Un regional sin tiendas de esa cadena dejaría la vista vacía sin decir por qué.
              const r = regional && !alcance.tiendas.some((x) => (!c || x.chain === c) && x.regional === regional) ? '' : regional;
              if (r !== regional) setRegional(r);
              // Lo mismo con el supervisor (21-oct-2026).
              if (!supervisorEn(c, r)) setSupervisor('');
            })
          }
          className={claseSelect}
        >
          <option value="">Todas las cadenas</option>
          {cadenas.map((c) => (
            <option key={c.valor} value={c.valor}>
              {c.etiqueta}
            </option>
          ))}
        </select>
        {flecha}
      </div>
    </Field>
  );

  // El administrador y dirección (21-oct-2026) ven las dos regionales: eligen una.
  const campoRegional = veTodasLasTiendas(alcance) ? (
    // 300 px: «Todos los regionales · 158 tiendas» cabe entera junto a la flecha (F5-V6).
    <Field etiqueta="Regional" htmlFor="metas-alcances-regional" className={esMovil ? 'w-full' : 'w-[300px]'}>
      <div className={claseCaja}>
        <select
          id="metas-alcances-regional"
          value={regional}
          onChange={(e) =>
            cambiarFiltro(() => {
              const r = e.target.value;
              setRegional(r);
              // El supervisor elegido que no es de ese regional se suelta (21-oct-2026).
              if (!supervisorEn(cadena, r)) setSupervisor('');
            })
          }
          className={claseSelect}
        >
          <option value="">{`Todos los regionales · ${textoTiendas(tiendasDeCadena.length)}`}</option>
          {regionales.map((r) => (
            <option key={r.nombre} value={r.nombre}>
              {`${nombrePropio(r.nombre)} · ${textoTiendas(r.tiendas)}`}
            </option>
          ))}
        </select>
        {flecha}
      </div>
    </Field>
  ) : misRegionales.length > 0 ? (
    // El regional ve el suyo: se nombra, no se elige.
    <Field etiqueta="Regional" className={esMovil ? 'w-full' : 'w-[260px]'}>
      <div className={cx(CLASES_CAMPO.off, 'max-md:h-metas-tactil')} data-testid="alcances-mi-regional">
        <span className="truncate">
          {misRegionales.map((r) => nombrePropio(r.nombre)).join(', ')} · {textoTiendas(tiendasDeCadena.length)}
        </span>
      </div>
    </Field>
  ) : null;

  // 21-oct-2026: el regional (y el administrador y dirección) ven sus tiendas
  // por supervisor; los indicadores y el Excel se rehacen en la API.
  const campoSupervisor = filtraSupervisor ? (
    <Field etiqueta="Supervisor" htmlFor="metas-alcances-supervisor" className={esMovil ? 'w-full' : 'w-[280px]'}>
      <div className={claseCaja}>
        <select
          id="metas-alcances-supervisor"
          value={supervisor}
          onChange={(e) => cambiarFiltro(() => setSupervisor(e.target.value))}
          className={claseSelect}
        >
          <option value="">{`Todos los supervisores · ${textoTiendas(tiendasDelRegional.length)}`}</option>
          {supervisores.map((s) => (
            <option key={s.nombre} value={s.nombre}>
              {`${nombrePropio(s.nombre)} · ${textoTiendas(s.tiendas)}`}
            </option>
          ))}
        </select>
        {flecha}
      </div>
    </Field>
  ) : null;

  const ventaAl = ventaAlTexto(datos?.ventaAl);
  const estadoSemana = datos ? (
    medible ? (
      <Pill tono="exito" icono={<IconoCandado />} className="h-7" title="La venta de los 7 días ya está materializada">
        Semana cerrada{ventaAl ? ` · venta al ${ventaAl}` : ''}
      </Pill>
    ) : (
      <Pill tono="aviso" icono={<IconoAviso />} className="h-7">
        {datos.cerrada ? 'Venta incompleta' : 'Semana sin cerrar'}
      </Pill>
    )
  ) : null;

  const botonExportar = (
    <button
      type="button"
      className={cx(CLASE_BOTON, esMovil && 'w-full')}
      onClick={() => void exportar()}
      disabled={!medible || !!bloqueoComisiones || descargando}
      title={bloqueoComisiones ?? (!medible && datos?.aviso ? datos.aviso : undefined)}
      aria-describedby={bloqueoComisiones ? 'alcances-comisiones-bloqueo' : undefined}
      data-testid="exportar-comisiones"
    >
      <IconoExcel />
      {descargando ? 'Exportando…' : 'Exportar para comisiones'}
    </button>
  );

  // ------------------------------------------------------------ KPIs
  const kpis = datos?.kpis ?? null;
  const tramos = datos?.tramos ?? [];
  const corte = corteEnMeta(tramos);
  const porBanda = useMemo(
    () =>
      [...(kpis?.porBanda ?? [])].sort((a, b) => ORDEN_BANDAS.indexOf(a.banda) - ORDEN_BANDAS.indexOf(b.banda)),
    [kpis],
  );
  const totalBandas = porBanda.reduce((s, b) => s + b.n, 0);

  const de = (n: number) => <>de {n}</>;
  const tarjetaKpi = (children: React.ReactNode, extra?: string) => (
    <div className={cx(CLASE_PANEL, 'min-w-0', extra)}>{children}</div>
  );

  const kpiAlcance = tarjetaKpi(
    <KpiCard
      etiqueta={etiquetaAlcanceGlobal(alcance, regional, supervisor)}
      valor={medible && kpis ? pctAlcance(kpis.alcanceRegional.pct) : '—'}
      detalle={
        medible && kpis ? (
          kpis.alcanceRegional.pct !== null ? (
            <span className="num">
              {pesosEnteros(kpis.alcanceRegional.venta)} de {pesosEnteros(kpis.alcanceRegional.meta)}
            </span>
          ) : (
            'Ninguna tienda tiene meta de tienda completa'
          )
        ) : (
          'Sin venta completa de la semana'
        )
      }
      className="max-md:px-4 max-md:py-4"
    />,
    'col-span-2 md:col-span-1',
  );

  const kpiTiendas = tarjetaKpi(
    <KpiCard
      etiqueta="Tiendas en meta"
      valor={medible && kpis ? kpis.tiendasEnMeta.n : '—'}
      complemento={medible && kpis ? de(kpis.tiendasEnMeta.de) : undefined}
      detalle={
        <>
          Alcance de {pctAlcance(corte).replace(/\.0 %$/, ' %')} o más
          {kpis && kpis.tiendasEnMeta.sinMeta > 0 && (
            <span className="block">
              {kpis.tiendasEnMeta.sinMeta === 1 ? '1 tienda sin meta de tienda' : `${kpis.tiendasEnMeta.sinMeta} tiendas sin meta de tienda`}
            </span>
          )}
        </>
      }
      className="max-md:px-4 max-md:py-4"
    />,
  );

  const kpiColaboradores = tarjetaKpi(
    <KpiCard
      etiqueta="Colaboradores en meta"
      valor={medible && kpis ? kpis.colaboradoresEnMeta.n : '—'}
      complemento={medible && kpis ? de(kpis.colaboradoresEnMeta.de) : undefined}
      detalle={
        <>
          {kpis && kpis.colaboradoresEnMeta.de === 0 && medible ? 'Nadie tiene meta autorizada con venta medible' : 'Con meta autorizada'}
          {kpis && kpis.colaboradoresEnMeta.sinClave > 0 && (
            <span className="block">
              {kpis.colaboradoresEnMeta.sinClave === 1
                ? '1 sin clave (su venta no se puede atribuir)'
                : `${kpis.colaboradoresEnMeta.sinClave} sin clave (su venta no se puede atribuir)`}
            </span>
          )}
        </>
      }
      className="max-md:px-4 max-md:py-4"
    />,
  );

  const kpiTramos = tarjetaKpi(
    <div role="group" aria-label="Colaboradores por tramo de alcance" className="flex min-w-0 flex-col gap-3 px-6 py-5 max-md:px-4 max-md:py-4">
      <span className="text-metas-etiqueta uppercase text-metas-texto-2">Colaboradores por tramo de alcance</span>
      {medible && totalBandas > 0 ? (
        <div role="img" aria-label={ariaTramos(porBanda)} className="flex h-3.5 gap-0.5 overflow-hidden rounded" data-testid="barra-tramos">
          {porBanda
            .filter((b) => b.n > 0)
            .map((b) => (
              <div key={b.banda} className={CLASE_FONDO_BANDA[b.banda]} style={{ flexGrow: b.n }} data-banda={b.banda} />
            ))}
        </div>
      ) : (
        <div className="h-3.5 rounded bg-metas-neutro-fondo" aria-hidden="true" />
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5" aria-hidden={medible && totalBandas > 0 ? true : undefined}>
        {porBanda.map((b) => {
          const t = tramos.find((x) => x.banda === b.banda);
          return (
            <div key={b.banda} className="flex items-center gap-1.5 whitespace-nowrap text-xs text-metas-texto-3" data-testid={`leyenda-${b.banda}`}>
              <span className={cx('h-2.5 w-2.5 shrink-0 rounded-[3px]', CLASE_FONDO_BANDA[b.banda])} />
              <span>
                <b className="num font-semibold text-metas-texto">{medible ? b.n : '—'}</b>{' '}
                {t ? etiquetaCortaTramo(t) : b.etiqueta}
              </span>
            </div>
          );
        })}
      </div>
      {medible && totalBandas === 0 && <span className="text-xs text-metas-texto-2">Nadie con meta autorizada y venta medible.</span>}
    </div>,
    'col-span-2 md:col-span-1',
  );

  // ------------------------------------------------------------ tiendas
  const visibles = verTodas ? tiendas : tiendas.slice(0, TIENDAS_A_LA_VISTA);

  const celdaPctTienda = (t: TiendaConAlcance) => {
    if (t.pct !== null) return <span className="num font-semibold">{pctAlcance(t.pct)}</span>;
    const m = motivoTiendaTexto(t.metaMotivo);
    // En la lista, la etiqueta corta («Sin meta»): la larga va en el `title` y para el lector.
    if (m) return <PillMotivo corto={esMovil ? m : motivoTiendaCorto(t.metaMotivo) ?? m} completo={motivoTiendaDetalle(t.metaMotivo)} envolver={esMovil} />;
    return <span className="text-metas-texto-2">—</span>;
  };

  const metaTiendaTexto = (t: TiendaConAlcance) =>
    t.meta !== null ? pesosEnteros(t.meta) : t.metaParcial !== null ? pesosEnteros(t.metaParcial) : '—';

  /** «Venta real $190,400 de $209,800»; sin meta utilizable, sólo la venta (la pastilla dice por qué). */
  const ventaContraMeta = (t: TiendaConAlcance) =>
    t.meta !== null ? `Venta real ${pesosEnteros(t.venta)} de ${pesosEnteros(t.meta)}` : `Venta real ${pesosEnteros(t.venta)}`;

  /**
   * El nombre de la fila para el lector, con las columnas dichas: la cabecera
   * de la lista es visual (`aria-hidden`) y, sin esto, se oían tres cifras
   * sueltas. Empieza por el nombre visible (WCAG 2.5.3, la etiqueta en el nombre).
   */
  const nombreAccesible = (t: TiendaConAlcance): string => {
    const final =
      t.pct !== null ? `alcance ${pctAlcance(t.pct)}` : (motivoTiendaTexto(t.metaMotivo) ?? 'sin alcance').toLowerCase();
    return `${nombreTienda(t)}: meta ${metaTiendaTexto(t)}, venta real ${pesosEnteros(t.venta)}, ${final}`;
  };

  const filaTienda = (t: TiendaConAlcance) => {
    const sel = mismaTienda(t, elegida);
    const nombre = nombreTienda(t);
    if (esMovil)
      return (
        <li key={`${t.chain}|${t.num}`}>
          <button
            type="button"
            aria-pressed={sel}
            aria-label={nombreAccesible(t)}
            onClick={() => elegir(t)}
            className={cx(
              'metas-foco flex w-full flex-col gap-2.5 rounded-metas-panel border bg-metas-panel p-3.5 text-left',
              sel ? 'border-metas-primario shadow-metas-anillo-sel' : 'border-metas-borde',
            )}
            data-tienda={`${t.chain}-${t.num}`}
          >
            <span className="flex w-full items-start justify-between gap-3">
              <span className="min-w-0 font-semibold [overflow-wrap:anywhere]">{nombre}</span>
              <span className="shrink-0">
                {celdaPctTienda(t)}
              </span>
            </span>
            <BarraAlcance pct={t.pct} banda={t.banda} />
            <span className="num text-xs text-metas-texto-2">
              {ventaContraMeta(t)}
            </span>
          </button>
        </li>
      );
    return (
      <li key={`${t.chain}|${t.num}`} className="border-t border-metas-borde-suave">
        <button
          type="button"
          aria-pressed={sel}
          aria-label={nombreAccesible(t)}
          onClick={() => elegir(t)}
          className={cx(
            'metas-foco grid w-full grid-cols-[minmax(0,1.1fr)_108px_108px_minmax(0,1fr)_120px] items-center text-left text-sm hover:bg-metas-cabecera',
            sel && '!bg-metas-fila-sel',
          )}
          data-tienda={`${t.chain}-${t.num}`}
        >
          {/* Recortado con «…»: el `title` lo deja leer con el ratón (F5-V4). */}
          <span className="truncate p-3 font-semibold" title={nombre}>
            {nombre}
          </span>
          <span className="num p-3 text-right text-metas-texto-2">
            {metaTiendaTexto(t)}
          </span>
          <span className="num p-3 text-right">
            {pesosEnteros(t.venta)}
          </span>
          <span className="py-3 pl-6 pr-3">
            <BarraAlcance pct={t.pct} banda={t.banda} />
          </span>
          <span className="flex justify-end p-3 text-right">
            {celdaPctTienda(t)}
          </span>
        </button>
      </li>
    );
  };

  const panelTiendas = (
    <section className={cx(!esMovil && CLASE_PANEL, 'min-w-0 overflow-hidden')} aria-label={TITULO_TIENDAS}>
      <div className={cx('flex flex-wrap items-baseline gap-x-3 gap-y-1', esMovil ? 'pb-3' : 'border-b border-metas-borde-suave px-4 py-3.5')}>
        <h2 className="m-0 text-[15px] font-semibold">{TITULO_TIENDAS}</h2>
        <span className="text-[13px] text-metas-texto-2">{SUBTITULO_TIENDAS}</span>
      </div>
      {!esMovil && (
        <div
          aria-hidden="true"
          className="grid grid-cols-[minmax(0,1.1fr)_108px_108px_minmax(0,1fr)_120px] bg-metas-cabecera text-metas-th text-metas-texto-2"
        >
          <div className="p-3">Tienda</div>
          <div className="p-3 text-right">Meta</div>
          <div className="p-3 text-right">Venta real</div>
          <div className="py-3 pl-6 pr-3">
            Alcance <span className="font-normal">(línea = 100 %)</span>
          </div>
          <div className="p-3 text-right">%</div>
        </div>
      )}
      {tiendas.length === 0 ? (
        <p className="m-0 px-4 py-6 text-sm text-metas-texto-2" data-testid="alcances-sin-tiendas">
          {cadena || regional || supervisor ? 'Ninguna tienda con esos filtros.' : 'No hay tiendas que mostrar.'}
        </p>
      ) : (
        <ul className={cx('m-0 list-none p-0', esMovil && 'flex flex-col gap-3')} aria-label="Tiendas ordenadas por alcance">
          {visibles.map(filaTienda)}
        </ul>
      )}
      {tiendas.length > 0 && (
        <div
          className={cx(
            'flex flex-wrap items-center justify-between gap-2 text-[13px] text-metas-texto-2',
            esMovil ? 'pt-3' : 'border-t border-metas-borde-suave px-4 py-3',
          )}
        >
          <span data-testid="alcances-mostrando">
            Mostrando {visibles.length} de {textoTiendas(tiendas.length)} · ordenado por alcance
          </span>
          {tiendas.length > TIENDAS_A_LA_VISTA && (
            <button type="button" className={CLASE_BOTON_TEXTO} onClick={() => setVerTodas((v) => !v)} aria-expanded={verTodas}>
              {verTodas ? 'Ver menos' : 'Ver todas'}
            </button>
          )}
        </div>
      )}
    </section>
  );

  // ------------------------------------------------------------ detalle por colaborador
  const filaElegida = tiendas.find((t) => mismaTienda(t, elegida)) ?? null;
  const cabeza: TiendaConAlcance | null = detalle ?? filaElegida;

  const celdaAlcancePersona = (c: ColaboradorConAlcance) =>
    c.pct !== null ? (
      <PctConBanda pct={c.pct} banda={c.banda} tramos={tramos} />
    ) : (
      <PillMotivo corto={motivoCorto(c) ?? '—'} completo={c.motivo} envolver={esMovil} />
    );

  const enEstaTienda = (total: number | null, aqui: number | null) =>
    total !== null && aqui !== null && Math.abs(total - aqui) >= 0.5 ? (
      <span className="block text-xs font-normal text-metas-texto-2">{pesosEnteros(aqui)} en esta tienda</span>
    ) : null;

  const nombrePersona = (c: ColaboradorConAlcance) =>
    c.esCasa ? (
      // La Casa (ronda F, 23-oct-2026): no es una persona y nadie la cobra; su venta ya está en la de la tienda.
      <>
        <span className="flex max-w-full items-center gap-1.5">
          <span className="min-w-0 truncate font-semibold" title={c.nombre}>
            {c.nombre}
          </span>
          <Pill tono="neutro" className="!h-5 !px-2 !text-[11px]">
            Casa
          </Pill>
        </span>
        <span className="block text-xs text-metas-texto-2" data-testid="alcance-casa">
          Casa de la tienda: no es una persona y nadie la cobra
        </span>
      </>
    ) : (
      <>
        <span className="block max-w-full truncate font-semibold" title={nombrePropio(c.nombre) || c.nombre}>
          {nombrePropio(c.nombre) || c.nombre}
        </span>
        <span className="block text-xs text-metas-texto-2">{c.puesto ? nombrePropio(c.puesto) : 'Sin puesto'}</span>
      </>
    );

  const listaColaboradores = (d: AlcanceDeTienda) => {
    if (!d.colaboradores.length)
      return (
        <p className="m-0 px-5 py-6 text-sm text-metas-texto-2" data-testid="alcances-sin-colaboradores">
          La tienda no tiene captura de metas en la semana {d.semana.numero}: no hay colaboradores que medir.
        </p>
      );
    if (esMovil)
      return (
        <ul className="m-0 flex list-none flex-col p-0" aria-label={`Colaboradores de ${nombreTienda(d)}`}>
          {d.colaboradores.map((c) => (
            <li key={c.key} className="flex flex-col gap-2.5 border-t border-metas-borde-suave px-4 py-3" data-colaborador={c.key}>
              <div className="min-w-0">{nombrePersona(c)}</div>
              <dl className="m-0 grid grid-cols-3 gap-2">
                <div className="min-w-0">
                  <dt className="text-[11px] font-semibold uppercase tracking-[.03em] text-metas-texto-2">Meta</dt>
                  <dd className="num m-0 text-sm">
                    {pesosEnteros(c.meta)}
                    {enEstaTienda(c.meta, c.metaEnTienda)}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-[11px] font-semibold uppercase tracking-[.03em] text-metas-texto-2">Venta</dt>
                  <dd className="num m-0 text-sm">
                    {pesosEnteros(c.venta)}
                    {enEstaTienda(c.venta, c.ventaEnTienda)}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-[11px] font-semibold uppercase tracking-[.03em] text-metas-texto-2">Alcance</dt>
                  <dd className="m-0 text-sm">{celdaAlcancePersona(c)}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      );
    return (
      <table className="w-full table-fixed border-collapse text-sm" aria-label={`Colaboradores de ${nombreTienda(d)}`}>
        <colgroup>
          <col />
          <col className="w-[96px]" />
          <col className="w-[96px]" />
          {/* Ancha para que la pastilla del motivo quepa en una línea (F5-V5). */}
          <col className="w-[172px]" />
        </colgroup>
        <thead className="bg-metas-cabecera text-metas-th text-metas-texto-2">
          <tr>
            <th scope="col" className="py-3 pl-5 pr-3 text-left font-semibold">
              Colaborador
            </th>
            <th scope="col" className="p-3 text-right font-semibold">
              Meta
            </th>
            <th scope="col" className="p-3 text-right font-semibold">
              Venta
            </th>
            <th scope="col" className="py-3 pl-3 pr-5 text-right font-semibold">
              Alcance
            </th>
          </tr>
        </thead>
        <tbody>
          {d.colaboradores.map((c) => (
            <tr key={c.key} className="border-t border-metas-borde-suave" data-colaborador={c.key}>
              <td className="min-w-0 py-3 pl-5 pr-3 align-middle">{nombrePersona(c)}</td>
              <td className="num p-3 text-right align-middle text-metas-texto-2">
                {pesosEnteros(c.meta)}
                {enEstaTienda(c.meta, c.metaEnTienda)}
              </td>
              <td className="num p-3 text-right align-middle">
                {pesosEnteros(c.venta)}
                {enEstaTienda(c.venta, c.ventaEnTienda)}
              </td>
              <td className="py-3 pl-3 pr-5 text-right align-middle">
                <span className="inline-flex justify-end">{celdaAlcancePersona(c)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const cuerpoDetalle = () => {
    if (!datos?.cerrada)
      return (
        <p className="m-0 px-5 py-6 text-sm text-metas-texto-2" role="status">
          {datos?.aviso ?? 'La semana no ha cerrado.'} El alcance por colaborador se mide cuando cierra.
        </p>
      );
    if (cargandoDetalle) return <p className="m-0 px-5 py-6 text-sm text-metas-texto-2">Cargando colaboradores…</p>;
    if (errorDetalle)
      return (
        <div className="flex flex-wrap items-center gap-3 px-5 py-6 text-sm" role="alert">
          <span className="text-metas-error">No se pudo leer la tienda: {errorDetalle}</span>
          <button type="button" className={CLASE_BOTON} onClick={() => void cargarDetalle()}>
            Reintentar
          </button>
        </div>
      );
    if (!detalle) return null;
    const noAutorizada = detalle.estado !== 'autorizada';
    return (
      <>
        {noAutorizada && (
          <div className="flex gap-2 border-b border-metas-banner-borde bg-metas-banner px-5 py-3 text-[13px] text-metas-banner-texto" role="status" data-testid="alcances-sin-autorizar">
            <IconoAviso />
            <span>
              Sin metas autorizadas: la tienda está <b>{(ETIQUETA_ESTADO[detalle.estado] ?? detalle.estado).toLowerCase()}</b> en la
              semana {detalle.semana.numero}. Ninguna de sus metas entra al cálculo de comisiones.
            </span>
          </div>
        )}
        {!detalle.completo && detalle.aviso && (
          <div className="flex gap-2 border-b border-metas-banner-borde bg-metas-banner px-5 py-3 text-[13px] text-metas-banner-texto" role="status">
            <IconoAviso />
            <span>{detalle.aviso}</span>
          </div>
        )}
        {listaColaboradores(detalle)}
      </>
    );
  };

  const panelDetalle = (
    <aside
      ref={refDetalle}
      className={cx(CLASE_PANEL, 'min-w-0 overflow-hidden', esMovil && 'scroll-mt-20')}
      aria-label={cabeza ? `Colaboradores de ${nombreTienda(cabeza)}` : 'Colaboradores de la tienda'}
      data-testid="alcances-detalle"
    >
      {!cabeza ? (
        <p className="m-0 px-5 py-6 text-sm text-metas-texto-2">
          {esMovil ? 'Toca una tienda para ver a sus colaboradores.' : 'Elige una tienda para ver a sus colaboradores.'}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-1 border-b border-metas-borde-suave px-5 py-4 max-md:px-4">
            <div className="flex items-start justify-between gap-3">
              <h2 className="m-0 min-w-0 text-lg font-semibold [overflow-wrap:anywhere]">{nombreTienda(cabeza)}</h2>
              <span className="shrink-0 text-lg">
                {cabeza.pct !== null ? (
                  <span className="num font-semibold" data-testid="alcance-tienda">
                    {pctAlcance(cabeza.pct)}
                  </span>
                ) : motivoTiendaTexto(cabeza.metaMotivo) ? (
                  <PillMotivo
                    corto={motivoTiendaTexto(cabeza.metaMotivo) as string}
                    completo={motivoTiendaDetalle(cabeza.metaMotivo)}
                    envolver={esMovil}
                  />
                ) : (
                  <span className="text-metas-texto-2">—</span>
                )}
              </span>
            </div>
            <span className="num text-[13px] text-metas-texto-3" data-testid="alcances-detalle-sub">
              Supervisor {nombrePropio(cabeza.supervisor) || '—'} ·{' '}
              {cabeza.meta !== null
                ? `${pesosEnteros(cabeza.venta)} de ${pesosEnteros(cabeza.meta)}`
                : `Venta real ${pesosEnteros(cabeza.venta)}`}
            </span>
          </div>
          {cuerpoDetalle()}
          <p className="m-0 border-t border-metas-borde-suave px-5 py-3.5 text-xs leading-normal text-metas-texto-2 max-md:px-4">
            {NOTA_ALCANCE}
          </p>
        </>
      )}
    </aside>
  );

  // ------------------------------------------------------------ render
  return (
    <div className="flex flex-col gap-4" data-testid="alcances-tab">
      <div className={cx('flex gap-3', esMovil ? 'flex-col' : 'flex-wrap items-end')}>
        {campoCadena}
        {campoRegional}
        {campoSupervisor}
        <div className="flex-grow" />
        {estadoSemana && <div className={cx(esMovil ? '' : 'self-center pb-1.5')}>{estadoSemana}</div>}
        {botonExportar}
      </div>

      {datos?.aviso && (
        <div
          className="flex gap-2 rounded-metas-control border border-metas-banner-borde bg-metas-banner px-4 py-3 text-sm text-metas-banner-texto"
          role="status"
          data-testid="alcances-aviso"
        >
          <IconoAviso />
          <span>{datos.aviso}</span>
        </div>
      )}

      {bloqueoComisiones && (
        // El porqué del botón deshabilitado, a la vista (no sólo en `title`).
        <p
          id="alcances-comisiones-bloqueo"
          className="m-0 flex gap-2 rounded-metas-control border border-metas-banner-borde bg-metas-banner px-4 py-3 text-sm text-metas-banner-texto"
          role="note"
          data-testid="alcances-comisiones-bloqueo"
        >
          <IconoAviso />
          <span>
            <strong className="font-semibold">Exportar para comisiones:</strong> {bloqueoComisiones}
          </span>
        </p>
      )}

      {datos?.plazo && !datos.aviso && (
        // F5-01: informativo, no bloquea: las cifras valen, pero lo autorizado aún puede cambiar.
        <p
          className="m-0 rounded-metas-control border border-metas-primario-suave bg-metas-info-fondo px-4 py-3 text-sm text-metas-info"
          role="note"
          data-testid="alcances-plazo"
        >
          {datos.plazo}
        </p>
      )}

      {error && !datos && (
        <div className="flex flex-wrap items-center gap-3" role="alert">
          <span className="text-sm text-metas-error">No se pudo leer el alcance: {error}</span>
          <button type="button" className={CLASE_BOTON} onClick={() => void cargar()}>
            Reintentar
          </button>
        </div>
      )}

      {!datos && cargando && <p className="m-0 text-sm text-metas-texto-2">Cargando alcances…</p>}

      {datos && (
        <>
          <div
            // En fila desde lg: a 768 partían «92.3 %» y «COLABORADORES» se salía de su tarjeta.
            className="grid grid-cols-2 gap-3 lg:grid-cols-[repeat(3,minmax(0,1fr))_minmax(0,1.6fr)] lg:gap-4"
            aria-busy={cargando || undefined}
            data-testid="alcances-kpis"
          >
            {kpiAlcance}
            {kpiTiendas}
            {kpiColaboradores}
            {kpiTramos}
          </div>

          <div className={cx(esMovil ? 'flex flex-col gap-4' : 'grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_560px]')}>
            {panelTiendas}
            {panelDetalle}
          </div>
        </>
      )}
    </div>
  );
};

export default AlcancesTab;
